import { ModalNL } from "@components/modal/Modal"
import { useGetOpModesListQuery } from "@queries/useGetOpModesListQuery"
import * as Accordion from "@radix-ui/react-accordion"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { Button, IconButton } from "@ui-lib/index"
import { SeempAction, SeempActionGroup, SeempReport } from "api"
import { t } from "i18next"
import { useEffect, useMemo, useRef, useState } from "react"
import { MdClose, MdDelete, MdFilterList, MdInfoOutline, MdSettings, MdSort } from "react-icons/md"
import { useSeempGetUsersQuery } from "./queries/useSeempGetUsersQuery"
import SeempActionCardPublished from "./SeempActionCardPublished"
import SeempActionGroupComponent from "./SeempActionGroup"
import SeempDeleteReportBody from "./SeempDeleteReportBody"
import SeempInfoCard from "./SeempInfoCard"
import { DropdownCheckboxItem } from "./SeempReportBody"
import SeempReportGenerator from "./SeempReportGenerator"
import SeempUpdateReportBody from "./SeempUpdateReportBody"

export default function SeempReportBodyPublished({
	plantId,
	reportData,
	actionGroups,
}: {
	plantId: number
	reportData: SeempReport
	actionGroups: SeempActionGroup[]
}) {
	const userData = useAppSelector(getUserData)
	const companyName = useAppSelector(getCurrentCompany)

	const [reportsDeviation, setReportsDeviation] = useState<
		{ actionId: number; deviation: number }[]
	>(
		reportData.actions.map((action) => {
			return { actionId: action.id, deviation: 0 }
		})
	)

	const handleDeviation = (actionId: number, deviation: number) => {
		setReportsDeviation((prev) => {
			if (prev.find((item) => item.actionId === actionId) === undefined) {
				return [...prev, { actionId, deviation }]
			}
			return prev.map((item) => (item.actionId === actionId ? { ...item, deviation } : item))
		})
	}

	const {
		isLoading: isUsersListLoading,
		isError: isUsersListError,
		data: usersList,
	} = useSeempGetUsersQuery({ companyName })

	const [isUpdateReportFormOpen, setIsUpdateReportFormOpen] = useState<boolean>(false)
	const [isDeleteReportFormOpen, setIsDeleteReportFormOpen] = useState<boolean>(false)
	const [isInfoCardOpen, setIsInfoCardOpen] = useState<boolean>(false)

	const {
		isLoading: isOpModesListLoading,
		isError: isOpModesListError,
		data: opModesList,
	} = useGetOpModesListQuery({ plantIdsList: [plantId] })

	const opModeIdsNameMap: { opModeIs: number[]; opModeLabel: string }[] = []
	const groupedActions: Record<string, SeempAction[]> = {}

	const [filterByCreatorList, setFilterByCreatorList] = useState<string[]>([])
	const [filterByResponsibleList, setFilterByResponsibleList] = useState<string[]>([])

	const filteredActions = reportData.actions.filter((a) =>
		filterByCreatorList.length > 0 || filterByResponsibleList.length > 0
			? filterByCreatorList.find((email) => email === a.createdBy) !== undefined ||
				filterByResponsibleList.find((email) => email === a.responsible) !== undefined
			: true
	)

	const actionIdGroupMap: { type: string[]; label: string }[] = []
	for (const group of actionGroups) {
		const type = group.actionsDefinitions.map((def) => def.type)
		actionIdGroupMap.push({
			type: type,
			label: group.label,
		})
	}

	for (const action of filteredActions) {
		if (opModesList === null || opModesList === undefined) {
			continue
		}
		const key =
			actionIdGroupMap.find((group) => group.type.includes(action.actionType))?.label ??
			"unknown"
		if (groupedActions[key] === undefined) {
			groupedActions[key] = []
		}
		groupedActions[key].push(action)
	}

	const [isGrouped, setIsGrouped] = useState<boolean>(true)
	const [sortOrder, setSortOrder] = useState<
		"alphAsc" | "alphDesc" | "deviationPositive" | "deviationNegative"
	>("alphAsc")

	const groupLabels = useMemo(() => Object.keys(groupedActions), [groupedActions])
	const actionIdStrings = useMemo(
		() => reportData.actions.map((action) => action.id.toString()),
		[reportData.actions]
	)
	const [openItems, setOpenItems] = useState<string[]>([])
	const hasInitializedAccordion = useRef(false)

	useEffect(() => {
		if (hasInitializedAccordion.current === false) {
			if (isGrouped === true && groupLabels.length > 0) {
				setOpenItems(groupLabels)
				hasInitializedAccordion.current = true
				return
			}
			if (isGrouped === false && actionIdStrings.length > 0) {
				setOpenItems(actionIdStrings)
				hasInitializedAccordion.current = true
			}
		}
	}, [isGrouped, groupLabels, actionIdStrings])

	useEffect(() => {
		if (hasInitializedAccordion.current === true) {
			if (isGrouped === true) {
				setOpenItems(groupLabels)
			} else {
				setOpenItems(actionIdStrings)
			}
		}
	}, [isGrouped])

	const listOfResponsibles = Array.from(
		new Set(reportData.actions.map((action) => action.responsible))
	).filter((email): email is string => email !== null && email !== undefined && email !== "")

	const listOfCreators = Array.from(
		new Set(reportData.actions.map((action) => action.createdBy))
	).filter((email): email is string => email !== null && email !== undefined && email !== "")

	const showDeviation = new Date(reportData.year, 1, 1) < new Date(new Date())

	return companyName !== undefined && usersList !== null && usersList !== undefined ? (
		<>
			<div
				className={`shadow-card sticky top-13 z-10 -mt-5 mb-5 -ml-5 flex h-12.25 w-[calc(100%+40px)] flex-row items-center ${reportData.isPublished === true ? "justify-between" : "justify-between"} gap-4 border-b-1 bg-white px-5`}
			>
				{reportData.isPublished === true ? (
					<SeempReportGenerator
						actionGroups={actionGroups}
						plantId={plantId}
						reportData={reportData}
						currentCompany={companyName}
						usersList={usersList}
						showDeviation={showDeviation}
						sortOrder={sortOrder}
						reportsDeviation={reportsDeviation}
						opModeIdsNameMap={opModeIdsNameMap}
					/>
				) : (
					<Button
						variant={"filled-secondary"}
						disabled
					>
						<Button.Label>{t("PUBLISH")}</Button.Label>
					</Button>
				)}
				<div className={`flex gap-2`}>
					{/* SORT DROPDOWN */}
					<DropdownMenu.Root>
						<DropdownMenu.Trigger asChild>
							<IconButton
								variant={"text-dark"}
								aria-label="sort actions"
							>
								<IconButton.Icon>{MdSort}</IconButton.Icon>
							</IconButton>
						</DropdownMenu.Trigger>
						<DropdownMenu.Portal>
							<DropdownMenu.Content
								align="end"
								alignOffset={0}
								className="text-dipai-secondary-901 border-dipai-secondary-901/20 data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade shadow-card z-1000 grid min-w-55 grid-cols-1 gap-0.5 rounded-sm border-1 bg-white p-2 will-change-[opacity,transform]"
								sideOffset={4}
							>
								<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pl-1 leading-6 font-semibold">
									{t("SORT BY")}
								</DropdownMenu.Label>
								<DropdownCheckboxItem
									checked={sortOrder === "alphAsc"}
									onCheckedChange={() => setSortOrder("alphAsc")}
									label={`${t("ALPHABETICAL")} ${t("ASC.")} (A→Z)`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "alphDesc"}
									onCheckedChange={() => setSortOrder("alphDesc")}
									label={`${t("ALPHABETICAL")} ${t("DSC.")} (Z→A)`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "deviationPositive"}
									onCheckedChange={() => setSortOrder("deviationPositive")}
									label={`${t("DEVIATION")} (${t("POSITIVE")}→${t("NEGATIVE")})`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={sortOrder === "deviationNegative"}
									onCheckedChange={() => setSortOrder("deviationNegative")}
									label={`${t("DEVIATION")} (${t("NEGATIVE")}→${t("POSITIVE")})`}
									showCheckBox={false}
								/>
								<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pt-1 pl-1 leading-6 font-semibold">
									{t("GROUP BY")}
								</DropdownMenu.Label>
								<DropdownCheckboxItem
									checked={isGrouped}
									onCheckedChange={() => setIsGrouped(true)}
									label={`${t("OPERATION MODE")}`}
									showCheckBox={false}
								/>
								<DropdownCheckboxItem
									checked={!isGrouped}
									onCheckedChange={() => setIsGrouped(false)}
									label={`${t("UNGROUPED")}`}
									showCheckBox={false}
								/>
							</DropdownMenu.Content>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
					{/* FILTER DROPDOWN */}
					<div className={`hover:bg-dipai-secondary-200 flex flex-row rounded-sm`}>
						<DropdownMenu.Root>
							<DropdownMenu.Trigger asChild>
								<div
									className={`hover:bg-dipai-secondary-100 flex h-8 flex-row items-center gap-1 pr-1`}
								>
									<IconButton
										variant={"text-dark"}
										aria-label="sort actions"
									>
										<IconButton.Icon>{MdFilterList}</IconButton.Icon>
									</IconButton>
									{filterByCreatorList.length !== 0 ||
									filterByResponsibleList.length !== 0 ? (
										<p
											className={`text-button text-dipai-secondary-901 cursor-pointer`}
										>
											{`${filteredActions.length} ${filteredActions.length === 1 ? t("ACTION") : t("ACTIONS")}`}
										</p>
									) : null}
								</div>
							</DropdownMenu.Trigger>
							<DropdownMenu.Portal>
								<DropdownMenu.Content
									align="end"
									alignOffset={
										filterByCreatorList.length !== 0 ||
										filterByResponsibleList.length !== 0
											? -32
											: 0
									}
									className="text-dipai-secondary-901 border-dipai-secondary-901/20 data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade shadow-card z-1000 grid max-h-[calc(100vh-10rem)] min-w-55 grid-cols-1 gap-0.5 overflow-auto rounded-sm border-1 bg-white p-2 will-change-[opacity,transform]"
									sideOffset={4}
								>
									<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pl-1 leading-6 font-semibold">
										{t("CREATED BY")}
									</DropdownMenu.Label>
									{listOfCreators.includes(userData.email) === false ? null : (
										<DropdownCheckboxItem
											checked={filterByCreatorList.includes(userData.email)}
											onCheckedChange={() =>
												setFilterByCreatorList((prev) => {
													if (prev.includes(userData.email)) {
														return prev.filter(
															(email) => email !== userData.email
														)
													}
													return [...prev, userData.email]
												})
											}
											label={`${t("ME")} (${userData.email})`}
											userAvatar={{
												userName:
													usersList.find(
														(u) => u.email === userData.email
													)?.name ?? t("UNKNOWN USER"),
												avatarColor: colorForUser(userData.email),
											}}
											onSelect={(e) => e.preventDefault()}
										/>
									)}
									{listOfCreators
										.filter((email) => email !== userData.email)
										.map((email) => (
											<DropdownCheckboxItem
												key={`creator_${email}`}
												checked={filterByCreatorList.includes(email)}
												onCheckedChange={() =>
													setFilterByCreatorList((prev) => {
														if (prev.includes(email)) {
															return prev.filter((e) => e !== email)
														}
														return [...prev, email]
													})
												}
												label={`${usersList.find((u) => u.email === email)?.name ?? t("UNKNOWN USER")} (${email})`}
												userAvatar={{
													userName:
														usersList.find((u) => u.email === email)
															?.name ?? t("UNKNOWN USER"),
													avatarColor: colorForUser(email),
												}}
												onSelect={(e) => e.preventDefault()}
											/>
										))}
									<DropdownMenu.Label className="text-dipai-secondary-901 text-tiny pt-1 pl-1 leading-6 font-semibold">
										{t("RESPONSIBLE PERSON")}
									</DropdownMenu.Label>
									{listOfResponsibles.includes(userData.email) ===
									false ? null : (
										<DropdownCheckboxItem
											checked={filterByResponsibleList.includes(
												userData.email
											)}
											onCheckedChange={() =>
												setFilterByResponsibleList((prev) => {
													if (prev.includes(userData.email)) {
														return prev.filter(
															(email) => email !== userData.email
														)
													}
													return [...prev, userData.email]
												})
											}
											label={`${t("ME")} (${userData.email})`}
											userAvatar={{
												userName:
													usersList.find(
														(u) => u.email === userData.email
													)?.name ?? t("UNKNOWN USER"),
												avatarColor: colorForUser(userData.email),
											}}
											onSelect={(e) => e.preventDefault()}
										/>
									)}
									{listOfResponsibles
										.filter((email) => email !== userData.email)
										.map((email) => (
											<DropdownCheckboxItem
												key={`responsible_${email}`}
												checked={filterByResponsibleList.includes(email)}
												onCheckedChange={() =>
													setFilterByResponsibleList((prev) => {
														if (prev.includes(email)) {
															return prev.filter((e) => e !== email)
														}
														return [...prev, email]
													})
												}
												label={`${usersList.find((u) => u.email === email)?.name ?? t("UNKNOWN USER")} (${email})`}
												userAvatar={{
													userName:
														usersList.find((u) => u.email === email)
															?.name ?? t("UNKNOWN USER"),
													avatarColor: colorForUser(email),
												}}
												onSelect={(e) => e.preventDefault()}
											/>
										))}
								</DropdownMenu.Content>
							</DropdownMenu.Portal>
						</DropdownMenu.Root>
						{filterByCreatorList.length !== 0 ||
						filterByResponsibleList.length !== 0 ? (
							<IconButton
								variant={"text-dark"}
								aria-label="clear filters"
								onClick={() => {
									setFilterByCreatorList([])
									setFilterByResponsibleList([])
								}}
							>
								<IconButton.Icon>{MdClose}</IconButton.Icon>
							</IconButton>
						) : null}
					</div>
					{/* DELETE BUTTON */}
					<IconButton
						variant={"text-dark"}
						// prettier-ignore
						disabled={
							reportData.isPublished === false ||
							(reportData.createdBy.toLowerCase() !== userData.email.toLowerCase() &&
							reportData.responsibleUsers.map((email) => email.toLowerCase()).includes(userData.email.toLowerCase()) === false)
						}
						onClick={() => setIsDeleteReportFormOpen(true)}
					>
						<IconButton.Icon>{MdDelete}</IconButton.Icon>
					</IconButton>
					{/* SETTING BUTTON */}
					<IconButton
						variant={"text-dark"}
						// prettier-ignore
						disabled={
							reportData.isPublished === false ||
							(reportData.createdBy.toLowerCase() !== userData.email.toLowerCase() &&
							reportData.responsibleUsers.map((email) => email.toLowerCase()).includes(userData.email.toLowerCase()) === false)
						}
						onClick={() => setIsUpdateReportFormOpen(true)}
					>
						<IconButton.Icon>{MdSettings}</IconButton.Icon>
					</IconButton>
					{/* INFO BUTTON */}
					<IconButton
						variant="text-dark"
						onClick={() => setIsInfoCardOpen(true)}
					>
						<IconButton.Icon>{MdInfoOutline}</IconButton.Icon>
					</IconButton>
				</div>
			</div>

			<Accordion.Root
				className="flex w-full flex-col gap-4"
				type="multiple"
				value={openItems}
				onValueChange={setOpenItems}
			>
				{isGrouped === true
					? Object.keys(groupedActions)
							.sort((a, b) => {
								if (
									sortOrder === "deviationPositive" ||
									sortOrder === "deviationNegative"
								) {
									const actionsA = groupedActions[a]
									const actionsB = groupedActions[b]
									if (actionsA === undefined || actionsB === undefined) {
										return 0
									}
									const deviationA = actionsA.reduce(
										(acc, action) =>
											acc +
											(reportsDeviation.find((e) => e.actionId === action.id)
												?.deviation ?? 0),
										0
									)
									const deviationB = actionsB.reduce(
										(acc, action) =>
											acc +
											(reportsDeviation.find((e) => e.actionId === action.id)
												?.deviation ?? 0),
										0
									)
									if (deviationA > deviationB)
										return -1 * (sortOrder === "deviationNegative" ? -1 : 1)
									if (deviationA < deviationB)
										return 1 * (sortOrder === "deviationNegative" ? -1 : 1)
									return a.localeCompare(b)
								}
								return sortOrder === "alphAsc"
									? a.localeCompare(b)
									: b.localeCompare(a)
							})
							.map((modeLabel) => {
								const actions = groupedActions[modeLabel]
								if (actions === undefined) {
									return null
								}
								return (
									<SeempActionGroupComponent
										key={`actionGroup_${modeLabel}`}
										modeLabel={modeLabel}
										totalDeviation={actions.reduce(
											(acc, action) =>
												acc +
												(reportsDeviation.find(
													(e) => e.actionId === action.id
												)?.deviation ?? 0),
											0
										)}
										showDeviation={showDeviation}
									>
										{actions
											.sort((a, b) => {
												const deviationA =
													reportsDeviation.find(
														(e) => e.actionId === a.id
													)?.deviation ?? 0
												const deviationB =
													reportsDeviation.find(
														(e) => e.actionId === b.id
													)?.deviation ?? 0
												if (deviationA > deviationB) return -1
												if (deviationA < deviationB) return 1

												return a.actionType.localeCompare(b.actionType)
											})
											.map((action) => (
												<SeempActionCardPublished
													key={`actionCard_${action.id}`}
													lockedActionList={[]}
													plantId={plantId}
													reportId={reportData.id}
													actionData={action}
													usersList={usersList}
													displayModeLabel={false}
													handleDeviation={handleDeviation}
													actionGroups={actionGroups}
													showDeviation={showDeviation}
													targetYear={reportData.year}
												/>
											))}
									</SeempActionGroupComponent>
								)
							})
					: filteredActions
							.sort((a, b) => {
								const aModeName = opModeIdsNameMap.find(
									(m) => `${m.opModeIs.sort()}` === `${a.opModeIds.sort()}`
								)?.opModeLabel
								const bModeName = opModeIdsNameMap.find(
									(m) => `${m.opModeIs.sort()}` === `${b.opModeIds.sort()}`
								)?.opModeLabel
								if (
									sortOrder === "deviationPositive" ||
									sortOrder === "deviationNegative"
								) {
									const deviationA =
										reportsDeviation.find((e) => e.actionId === a.id)
											?.deviation ?? 0
									const deviationB =
										reportsDeviation.find((e) => e.actionId === b.id)
											?.deviation ?? 0
									if (deviationA > deviationB)
										return -1 * (sortOrder === "deviationNegative" ? -1 : 1)
									if (deviationA < deviationB)
										return 1 * (sortOrder === "deviationNegative" ? -1 : 1)
									if (aModeName === undefined || bModeName === undefined) {
										return 0
									}
									return `${aModeName}: ${a.actionType}`.localeCompare(
										`${bModeName}: ${b.actionType}`
									)
								}
								if (aModeName === undefined || bModeName === undefined) {
									return 0
								}
								return sortOrder === "alphAsc"
									? `${aModeName}: ${a.actionType}`.localeCompare(
											`${bModeName}: ${b.actionType}`
										)
									: `${bModeName}: ${b.actionType}`.localeCompare(
											`${aModeName}: ${a.actionType}`
										)
							})
							.map((action) => (
								<SeempActionCardPublished
									key={`actionCard_${action.id}`}
									lockedActionList={[]}
									plantId={plantId}
									reportId={reportData.id}
									actionData={action}
									usersList={usersList}
									handleDeviation={handleDeviation}
									actionGroups={actionGroups}
									showDeviation={showDeviation}
									targetYear={reportData.year}
								/>
							))}
			</Accordion.Root>
			<ModalNL
				isOpen={isUpdateReportFormOpen}
				setIsOpen={setIsUpdateReportFormOpen}
				opacity={0.75}
			>
				<SeempUpdateReportBody
					companyName={companyName}
					reportData={reportData}
					plantId={plantId}
					setModalOpenState={setIsUpdateReportFormOpen}
				/>
			</ModalNL>
			<ModalNL
				isOpen={isDeleteReportFormOpen}
				setIsOpen={setIsDeleteReportFormOpen}
				opacity={0.75}
			>
				<SeempDeleteReportBody
					companyName={companyName}
					reportData={reportData}
					plantId={plantId}
					setModalOpenState={setIsDeleteReportFormOpen}
				/>
			</ModalNL>
			<ModalNL
				isOpen={isInfoCardOpen}
				setIsOpen={setIsInfoCardOpen}
				opacity={0.75}
			>
				<SeempInfoCard setModalOpenState={setIsInfoCardOpen} />
			</ModalNL>
		</>
	) : (
		"loading"
	)
}

function hash(str: string) {
	let h = 5381
	for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i)
	return h >>> 0
}

export function colorForUser(id: string) {
	const hue = hash(id) % 360
	const s = 65
	const l = 45
	return `hsl(${hue}deg ${s}% ${l}%)`
}
